import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { getImageUrl } from '../../services/imageService';
import Icon from '../common/Icon';
import ProductPicker from './ProductPicker';
import CampaignPreviewModal from './CampaignPreviewModal';
import './CampaignForm.css';

const PLACEMENTS = [
  { value: 'hero', label: 'Hero slider (top of home page)' },
  { value: 'strip', label: 'Promo strip (row of small cards)' },
  { value: 'wide', label: 'Wide banner (full-width)' },
  { value: 'section', label: 'Product section (shows products inline)' },
];

const BLANK = {
  title: '',
  kicker: '',
  subtitle: '',
  cta_text: 'Shop Now',
  placement: 'strip',
  external_link: '',
  text_theme: 'light',
  text_position: 'left',
  overlay_opacity: 35,
  display_order: 0,
  is_active: true,
  start_date: '',
  end_date: '',
};

// <input type="datetime-local"> speaks 'YYYY-MM-DDTHH:mm' in local time, while
// the API speaks ISO. Convert on both edges rather than in the markup.
const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) =>
  value ? new Date(value).toISOString() : '';

const CampaignForm = ({ mode, campaign, onSubmit }) => {
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState(BLANK);
  const [selectedIds, setSelectedIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mobileImage, setMobileImage] = useState(null);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load the product catalogue for the picker
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await api.get('/admin/products', {
          params: { limit: 200 },
        });
        setProducts(response.data.data.products || []);
      } catch (err) {
        setError(
          'Could not load products for selection: ' + (err.message || 'Unknown error')
        );
      }
    };
    loadProducts();
  }, []);

  // Hydrate from an existing campaign when editing
  useEffect(() => {
    if (!campaign) return;

    setFormData({
      title: campaign.title || '',
      kicker: campaign.kicker || '',
      subtitle: campaign.subtitle || '',
      cta_text: campaign.cta_text || 'Shop Now',
      placement: campaign.placement || 'strip',
      external_link: campaign.external_link || '',
      text_theme: campaign.text_theme || 'light',
      text_position: campaign.text_position || 'left',
      overlay_opacity:
        campaign.overlay_opacity === undefined ? 35 : campaign.overlay_opacity,
      display_order: campaign.display_order ?? 0,
      is_active: campaign.is_active !== false,
      start_date: toLocalInput(campaign.start_date),
      end_date: toLocalInput(campaign.end_date),
    });

    const items = campaign.items || [];
    setSelectedIds(
      [...items]
        .sort((a, b) => a.display_order - b.display_order)
        .map((item) => item.product_id)
    );

    if (campaign.image_url) setImagePreview(getImageUrl(campaign.image_url));
    if (campaign.mobile_image_url) {
      setMobileImagePreview(getImageUrl(campaign.mobile_image_url));
    }
  }, [campaign]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const makeImageHandler = (setFile, setPreview) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.title.trim()) {
        throw new Error('Campaign title is required');
      }
      if (!isEdit && !image) {
        throw new Error('A desktop image is required');
      }
      if (
        formData.start_date &&
        formData.end_date &&
        new Date(formData.end_date) <= new Date(formData.start_date)
      ) {
        throw new Error('The end date must be after the start date');
      }
      if (!formData.external_link.trim() && selectedIds.length === 0) {
        throw new Error(
          'Pick at least one product, or set an external link for this campaign to point at'
        );
      }

      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('kicker', formData.kicker || '');
      payload.append('subtitle', formData.subtitle || '');
      payload.append('cta_text', formData.cta_text || '');
      payload.append('placement', formData.placement);
      payload.append('external_link', formData.external_link || '');
      payload.append('text_theme', formData.text_theme);
      payload.append('text_position', formData.text_position);
      payload.append('overlay_opacity', String(formData.overlay_opacity));
      payload.append('display_order', String(Number(formData.display_order) || 0));
      payload.append('is_active', String(formData.is_active));
      payload.append('start_date', fromLocalInput(formData.start_date));
      payload.append('end_date', fromLocalInput(formData.end_date));
      payload.append('product_ids', JSON.stringify(selectedIds));

      if (image) payload.append('image', image);
      if (mobileImage) payload.append('mobile_image', mobileImage);

      await onSubmit(payload);
      navigate('/campaigns');
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} campaign`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-campaign-container">
      <div className="page-header">
        <h1 className="page-title">
          {isEdit ? 'Edit Campaign' : 'Create Campaign'}
        </h1>
        <div className="page-header-actions">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="btn-preview"
          >
            <Icon name="eye" /> Preview
          </button>
          <button onClick={() => navigate('/campaigns')} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>

      <CampaignPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        formData={formData}
        desktopImage={imagePreview}
        mobileImage={mobileImagePreview}
        products={products}
        selectedIds={selectedIds}
      />

      <div className="form-container">
        <form onSubmit={handleSubmit} className="campaign-form">
          <fieldset className="form-section">
            <legend>Content</legend>

            <div className="form-group">
              <label htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g. Monsoon Sale"
              />
              <small className="form-hint">
                Also the heading on the campaign&apos;s own page.
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="kicker">Kicker</label>
                <input
                  type="text"
                  id="kicker"
                  name="kicker"
                  value={formData.kicker}
                  onChange={handleChange}
                  placeholder="e.g. Limited time"
                />
              </div>

              <div className="form-group">
                <label htmlFor="cta_text">Button text</label>
                <input
                  type="text"
                  id="cta_text"
                  name="cta_text"
                  value={formData.cta_text}
                  onChange={handleChange}
                  placeholder="Shop Now"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subtitle">Subtitle</label>
              <textarea
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                rows={2}
                placeholder="One supporting line of copy"
              />
            </div>
          </fieldset>

          <fieldset className="form-section">
            <legend>Placement &amp; schedule</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="placement">
                  Placement <span className="required">*</span>
                </label>
                <select
                  id="placement"
                  name="placement"
                  value={formData.placement}
                  onChange={handleChange}
                >
                  {PLACEMENTS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="display_order">Display order</label>
                <input
                  type="number"
                  id="display_order"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                  min="0"
                />
                <small className="form-hint">Lower numbers show first.</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Starts</label>
                <input
                  type="datetime-local"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
                <small className="form-hint">Leave blank to start immediately.</small>
              </div>

              <div className="form-group">
                <label htmlFor="end_date">Ends</label>
                <input
                  type="datetime-local"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
                <small className="form-hint">Leave blank to run indefinitely.</small>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                Active
              </label>
              <small className="form-hint">
                A campaign only shows when it is active <em>and</em> inside its dates.
              </small>
            </div>
          </fieldset>

          <fieldset className="form-section">
            <legend>Artwork</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="image">
                  Desktop image {!isEdit && <span className="required">*</span>}
                </label>
                {imagePreview ? (
                  <div className="image-preview-container">
                    <img src={imagePreview} className="image-preview" alt="Desktop preview" />
                    <button
                      type="button"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                      className="btn-remove-image"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="image"
                      accept="image/*"
                      onChange={makeImageHandler(setImage, setImagePreview)}
                      className="file-input"
                    />
                    <label htmlFor="image" className="file-upload-label">
                      <Icon name="image" size={26} className="upload-icon" />
                      <span>Click to upload desktop image</span>
                      <span className="file-hint">JPG, PNG, WEBP (Max 5MB)</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mobile_image">Mobile image</label>
                {mobileImagePreview ? (
                  <div className="image-preview-container">
                    <img
                      src={mobileImagePreview}
                      className="image-preview"
                      alt="Mobile preview"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMobileImage(null);
                        setMobileImagePreview(null);
                      }}
                      className="btn-remove-image"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="mobile_image"
                      accept="image/*"
                      onChange={makeImageHandler(setMobileImage, setMobileImagePreview)}
                      className="file-input"
                    />
                    <label htmlFor="mobile_image" className="file-upload-label">
                      <Icon name="smartphone" size={26} className="upload-icon" />
                      <span>Click to upload mobile image</span>
                      <span className="file-hint">Optional - falls back to desktop</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="text_theme">Text colour</label>
                <select
                  id="text_theme"
                  name="text_theme"
                  value={formData.text_theme}
                  onChange={handleChange}
                >
                  <option value="light">Light text (for dark images)</option>
                  <option value="dark">Dark text (for light images)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="text_position">Text position</label>
                <select
                  id="text_position"
                  name="text_position"
                  value={formData.text_position}
                  onChange={handleChange}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="overlay_opacity">
                  Image shading: {formData.overlay_opacity}%
                </label>
                <input
                  type="range"
                  id="overlay_opacity"
                  name="overlay_opacity"
                  value={formData.overlay_opacity}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="5"
                />
                <small className="form-hint">
                  Higher values darken the image so the text stays readable.
                </small>
              </div>
            </div>
          </fieldset>

          <fieldset className="form-section">
            <legend>Products</legend>

            <div className="form-group">
              <label htmlFor="external_link">External link (optional)</label>
              <input
                type="text"
                id="external_link"
                name="external_link"
                value={formData.external_link}
                onChange={handleChange}
                placeholder="Leave blank to open this campaign's own product page"
              />
              <small className="form-hint">
                Set this only if the banner should point somewhere else entirely.
              </small>
            </div>

            <ProductPicker
              products={products}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
            />
          </fieldset>

          {error && (
            <div className="error-message-bottom">
              <Icon name="alert" /> <span>{error}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/campaigns')}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading
                ? isEdit
                  ? 'Saving...'
                  : 'Creating...'
                : isEdit
                  ? 'Save Campaign'
                  : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;
